import { opMoment, opTime } from 'utils/dates';

// Monthly samples across a year and a month, so the probe always spans both
// daylight and standard time no matter when the page happens to be opened.
const PROBE_MONTH_COUNT = 13;

/**
 * The zones that moved to permanent daylight time in tzdata 2026e, plus their
 * tzdata aliases.
 *
 * Deliberately an explicit list rather than "any zone the browser disagrees
 * with": that broader check also fires on unrelated zones, notably
 * Africa/Casablanca and Africa/El_Aaiun, whose Ramadan-based shifts drift from
 * shipped browser data every year. Those users would get a banner about a
 * Canadian DST change, and older browsers disagree on more zones still.
 */
const AFFECTED_TIME_ZONES = new Set([
  'America/Edmonton',
  'America/Inuvik',
  'America/Rainy_River', // alias of America/Winnipeg
  'America/Vancouver',
  'America/Winnipeg',
  'America/Yellowknife', // alias of America/Edmonton
  'Canada/Central', // alias of America/Winnipeg
  'Canada/Mountain', // alias of America/Edmonton
  'Canada/Pacific', // alias of America/Vancouver
]);

/**
 * The time zone the browser reports for itself, or `undefined` when it cannot
 * be read (a locked-down or very old `Intl` implementation).
 */
export const resolveBrowserTimeZone = (): string | undefined => {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || undefined;
  } catch {
    return undefined;
  }
};

interface IsBrowserTimezoneDataStaleOptions {
  /**
   * The browser's own time zone. The two offsets are only comparable when this
   * is the zone the browser is actually running in.
   */
  timeZone?: string;
  /**
   * Resolves the UTC offset the browser itself applies at an instant -- the
   * exact value the access date conversions depend on. Injectable so tests can
   * simulate a stale browser without changing the process time zone.
   */
  getBrowserOffsetMinutes?: (isoInstant: string) => number;
  /** Start of the probe window. Defaults to now. */
  from?: Date;
}

/**
 * Detects a browser in one of the `AFFECTED_TIME_ZONES` whose built-in time
 * zone rules disagree with the up-to-date rules bundled in moment-timezone.
 *
 * `createZuluStringFromLocalZuluString` recovers an access date's wall time
 * using the browser's own UTC offset, so a browser with stale rules saves and
 * redisplays that date an hour off. The offset comparison is kept alongside
 * the zone list so the warning disappears on its own as browsers pick up
 * tzdata 2026e, instead of nagging users who are already correct.
 */
export const isBrowserTimezoneDataStale = ({
  timeZone = resolveBrowserTimeZone(),
  getBrowserOffsetMinutes = (isoInstant: string) =>
    opTime(isoInstant).utcOffset(),
  from,
}: IsBrowserTimezoneDataStaleOptions = {}): boolean => {
  // Everything outside the affected regions is somebody else's tzdata drift.
  if (!timeZone || !AFFECTED_TIME_ZONES.has(timeZone)) return false;

  const probeStart = opMoment(from).startOf('month');

  for (let month = 0; month < PROBE_MONTH_COUNT; month += 1) {
    // Midday keeps the probe clear of the overnight hours transitions land on.
    const isoInstant = probeStart
      .clone()
      .add(month, 'months')
      .hour(12)
      .toISOString();

    try {
      if (
        getBrowserOffsetMinutes(isoInstant) !==
        opMoment.tz(isoInstant, timeZone).utcOffset()
      ) {
        return true;
      }
    } catch {
      // A browser that cannot report an offset is not evidence of stale rules.
      return false;
    }
  }

  return false;
};
